import z from 'zod';
import type { QuantFileTableData } from '../api/interfaces/projects.quant-analysis';
import type { BarChart, MultipleBarChart, PieChart } from './chat.chart';
import type { Widget } from './project.widgets';

export enum SegmentationDashboardWidget {
  ChartVisualization = 'chart-visualization',
  Quote = 'quote',
  RichText = 'rich-text',
}

export interface WidgetPropsByType {
  [SegmentationDashboardWidget.ChartVisualization]: Widget.ChartVisualizationProps;
  [SegmentationDashboardWidget.Quote]: Widget.QuoteProps;
  [SegmentationDashboardWidget.RichText]: Widget.RichTextProps;
}

export interface BaseWidgetInstance<T extends SegmentationDashboardWidget> {
  id: number;
  type: T;
  title: string;
  props?: WidgetPropsByType[T];
  sourceMetadata?: WidgetSourceMetadata;
}

export type WidgetItem =
  | Widget.ChartVisualization
  | Widget.Quote
  | Widget.RichText;

export type WidgetChart = BaseWidgetInstance<SegmentationDashboardWidget.ChartVisualization>;

export type WidgetInstance = {
  [T in SegmentationDashboardWidget]: BaseWidgetInstance<T>;
}[SegmentationDashboardWidget];


// Inline content types
export const RichTextContentSchema = z.object({
  type: z.literal('rich-text'),
  data: z.object({
    content: z.record(z.string(), z.unknown()).optional(),
    title: z.string().optional(),
  }),
});
export type RichTextContent = z.infer<typeof RichTextContentSchema>;

const InlineContentTypes = z.discriminatedUnion('type', [
  RichTextContentSchema,
]);

export const InlineContentSchema = z.object({
  id: z.string(),
}).and(InlineContentTypes);
export type InlineContent = z.infer<typeof InlineContentSchema>;

// Union type for section content items
export const SectionContentItemSchema = z.union([
  z.object({
    type: z.literal('widget'),
    widgetId: z.number(),
    width: z.number().optional(),
  }),
  z.object({
    type: z.literal('inline'),
    content: InlineContentSchema,
    width: z.number().optional(),
  }),
]);
export type SectionContentItem = z.infer<typeof SectionContentItemSchema>;


export const SectionLayoutSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  tag: z
    .object({
      tagId: z.number(),
      tagName: z.string(),
    })
    .optional(),
  contentItems: z.array(SectionContentItemSchema),
});
export type SectionLayout = z.infer<typeof SectionLayoutSchema>;

export const BlockLayoutSchema = z.object({
  id: z.string(),
  title: z.string().optional(),

  tag: z
    .object({
      tagId: z.number(),
      tagName: z.string(),
    })
    .optional(),
  sections: z.array(z.lazy(() => SectionLayoutSchema)).optional(),
  widgetId: z.number().optional(),
  inlineContent: InlineContentSchema.optional(),
});
export type BlockLayout = z.infer<typeof BlockLayoutSchema>;


export const WidgetGridLayoutSchema = z.object({
  blocks: z.array(BlockLayoutSchema),
});
export type WidgetGridLayout = z.infer<typeof WidgetGridLayoutSchema>;

// Widget Source Metadata Types
export enum WidgetSourceType {
  QuantAnalysis = 'quant-analysis',
  BrandInsights = 'brand-insights',
  Manual = 'manual',
  Other = 'other',
}

export interface QuantAnalysisFilterInfo {
  columnNames?: string[];
  rowNames?: string[];
}

export interface QuantAnalysisSourceInfo {
  projectId: number;
  projectName: string;
  quantAnalysisId: number;
  quantAnalysisName: string;
  itemId: number;
  itemName: string;
  filters?: QuantAnalysisFilterInfo;
}

export interface QuantAnalysisWidgetSourceMetadata {
  type: WidgetSourceType.QuantAnalysis;
  createdAt: string;
  quantAnalysis: QuantAnalysisSourceInfo;
}

export interface BrandInsightsWidgetSourceMetadata {
  type: WidgetSourceType.BrandInsights;
  createdAt: string;
}

export interface ManualWidgetSourceMetadata {
  type: WidgetSourceType.Manual;
  createdAt: string;
}

export interface OtherWidgetSourceMetadata {
  type: WidgetSourceType.Other;
  createdAt: string;
}

export type WidgetSourceMetadata =
  | QuantAnalysisWidgetSourceMetadata
  | BrandInsightsWidgetSourceMetadata
  | ManualWidgetSourceMetadata
  | OtherWidgetSourceMetadata;

export type DataTableSortConfig = {
  columnIndex: number;
  direction: 'asc' | 'desc';
};

// QuickChartData type definition (simplified for this context)
export type QuickChartData =
  | { type: 'bar'; chart: BarChart; options?: { n?: number; unit?: string; isPrefix?: boolean; }; insights?: string }
  | { type: 'stacked-bar'; chart: BarChart; options?: { n?: number; unit?: string; isPrefix?: boolean }; insights?: string }
  | { type: 'pie'; chart: PieChart; options?: { n?: number; unit?: string; isPrefix?: boolean }; insights?: string }
  | { type: 'multiple-clustered-bar'; chart: MultipleBarChart; options?: { seriesToN?: Record<string, number>; unit?: string; isPrefix?: boolean }; insights?: string }
  | { type: 'multiple-stacked-bar'; chart: MultipleBarChart; options?: { seriesToN?: Record<string, number>; unit?: string; isPrefix?: boolean }; insights?: string }
  | { type: 'data-table'; title: string; headerColor: string; data: QuantFileTableData; customColumnNames?: string[][]; customRowNames?: string[][]; sortConfig?: DataTableSortConfig | null; insights?: string }
  | { type: 'kpi-widget'; title: string; backgroundColor: string; values: string[]; labels: string[]; insights?: string }
  | { type: 'progress-bar-widget'; title: string; backgroundColor: string; values: string[]; labels: string[]; colors?: string[]; minValues?: string[]; maxValues?: string[]; units?: string[]; insights?: string };
